import axios from "axios";
import { getAuthType } from "@/composables/mail";
import { getField, updateField } from "vuex-map-fields";
import router from "../router";
import i18n from "../i18n";
import { signInEvents, signOutEvents } from "@/composables/events/handler";

const debug = false;

const IDLE_TIMEOUT = debug ? 600000 : 43200000; // 12 hours (10 minutes in debug)

if (debug) {
	console.warn("=================================================");
	console.warn("JWT refresh and idle timers running in debug mode");
	console.warn("Enable verbose in the chrome console to view logs");
	console.warn("=================================================");
}

export const roles = {
	ADMIN : "admin",
	SUPER_ADMIN : "super-admin",
	DEVOPS: "devops",
	MARKETING: "marketing"
};

let idleTimeout;

export default {
	namespaced: true,
	state: () => ({
		jwt: null,
		expires: null,
		eventsToken: null,
		forgottenEmail: null,
		logoutTrigger: null,
		userStatus: "offline",

		profile: {},
		userGroup: {},
		colors: {},

		// Not persistent in localstore, see stores/index.js
		refreshed: false,
		switchingUserGroup: false
	}),
	mutations: {
		updateField,
		setJwt(state, jwt) {
			state.jwt = jwt;

			if (jwt?.length) {
				const [, payloadB64] = jwt.split(".");

				state.expires = JSON.parse(atob(payloadB64))?.expires;
				if (debug) console.debug(`Set JWT token ${jwt} Expires at ${state.expires}`);
			} else {
				state.expires = undefined;
			}
		},
		setColors(state, colors) {
			state.colors = colors;
		},
		setUser(state, user) {
			state.profile = user;
		},
		updateUser(state, user) {
			state.profile = {
				...state.profile,
				...user
			};
			const existing = state.colors[user.id];

			state.colors[user.id] = {
				...(typeof existing === "object" && existing !== null ? existing : {}),
				userId: user.id,
				color: user.color ?? "",
				fullname: user.fullname ?? existing?.fullname ?? ""
			};
		},

		setUserGroup(state, userGroup) {
			state.userGroup = { ...userGroup };
		},

		setSwitchingUserGroup(state, value) {
			state.switchingUserGroup = value;
		},

		setEventsToken(state, eventsToken) {
			state.eventsToken = eventsToken;
		},

		setUserGroupEmail(state, { email, emailProvider, valid }) {
			if (!state.userGroup?.id) return;

			state.userGroup = {
				...state.userGroup,
				email,
				emailProvider,
				valid
			};
		},

		setUserStatus(state, status) {
			if (state.userStatus !== status) {
				axios.put("/auth/status", { status }).then(() =>{
					state.userStatus = status;
				});
			}
		},
		setForgottenEmail: (state, payload) => (state.forgottenEmail = payload),
		setLogoutTrigger: (state, payload) => (state.logoutTrigger = payload),
		setRefreshed: (state, value) => (state.refreshed = value),
		clear(state) {
			state.jwt = null;
			state.expires = null;
			state.eventsToken = null;
			state.forgottenEmail = null;
			state.userStatus = "offline";

			state.profile = {};
			state.userGroup = {};
			state.colors = {};

			// Not persistent in localstore, see stores/index.js
			state.refreshed = false;
			sessionStorage.clear();
		}
	},
	actions: {
		async onRefresh({ state, dispatch, commit, getters }) {
			console.log("[LOGIN] Restoring session on page refresh");

			if (!getters.loggedIn) {
				console.log("[LOGIN] No active session found on page refresh, redirecting to login page");
				return dispatch("sessionExpired");
			}

			try {
				if (state.eventsToken) {
					try {
						await signInEvents(state.eventsToken);
					} catch (err) {
						if (err?.code === "auth/invalid-custom-token") {
							console.debug("[LOGIN] Events token expired, refreshing...");
							const { data: { eventsToken } } = await axios.put("/auth/user-group", { userGroup: state.userGroup.id });

							commit("setEventsToken", eventsToken);
							await signInEvents(eventsToken);
						} else {
							throw err;
						}
					}
				}

				// Signal stores that refresh is complete
				commit("setRefreshed", true);

				console.log("[LOGIN] Session restored successfully");
			} catch (err) {
				console.error("[LOGIN] Failed to restore session:", err);
				await dispatch("sessionExpired");
			}
		},

		async authenticate({ commit, dispatch }, loginResponse) {
			const { jwt, eventsToken, user, userGroup, organization } = loginResponse;

			commit("setJwt", jwt);

			await Promise.all([
				commit("clinic/setCurrent", organization, { root: true }),
				commit("setUser", user),
				dispatch("assignUserGroup", { userGroup, eventsToken }),
				dispatch("getAssignedColors")
			]);
		},

		async endSession(_, options = {}) {
			try {
				const { fromAllDevices } = options;

				await axios.post("/auth/logout", {
					revokeAll: !!fromAllDevices
				});
			} catch (err) {
				console.error("Failed to logout user:", err);
			}
		},
		async logout({ dispatch }) {
			await dispatch("removeTypedMessages", { userId: this.profile?.id });
			await dispatch("endSession");
			await dispatch("showLogin");
		},
		async removeTypedMessages(_, { userId }) {
			for (let i = 0; i < localStorage.length; i++) {
				if (localStorage.key(i).startsWith(userId + "_typed_")) {
					localStorage.removeItem(localStorage.key(i));
				}
			}
		},
		async refreshToken({ commit, getters, dispatch }) {
			try {
				const { data: { jwt } } = await axios.post("/auth/refresh", {}, {
					timeout: 30000
				});

				if (!jwt?.length) {
					throw new Error("Invalid jwt");
				}

				// Only set new jwt when user still logged in
				if (getters.loggedIn) {
					commit("setJwt", jwt);
				}

				return jwt;
			} catch (err) {
				console.error("Failed to refresh token:", err);
				dispatch("sessionExpired");
			}
		},
		async sessionExpired({ dispatch }) {
			try {
				await dispatch("clearAll");
			} catch (err) {
				console.error("Failed to clean up session:", err);
			} finally {
				if (router.currentRoute.name !== "login") {
					router.replace({
						name: "login",
						params: { "session-expired": Date.now() }
					});
				}
			}
		},
		async showLogin({ dispatch }) {
			await dispatch("clearAll");

			if (router.currentRoute.name !== "login") {
				router.go("login");
			}
		},
		async expireSession({ dispatch }) {
			await dispatch("endSession");
			await dispatch("sessionExpired");
		},
		async activity({ dispatch }) {
			if (debug) console.debug("Activity!");
			await dispatch("resetIdleTimeout");
		},
		clearIdleTimeout() {
			if (debug) console.debug("Clearing idle timeout");
			clearTimeout(idleTimeout);
			idleTimeout = undefined;
		},
		async clearTimers({ dispatch }) {
			await dispatch("clearIdleTimeout");
		},
		async resetIdleTimeout({ state, dispatch }) {
			if (debug) console.debug("Reset Idle timeout");
			await dispatch("clearIdleTimeout");

			if (!state.connected) return;

			idleTimeout = setTimeout(() => {
				dispatch("expireSession");
			}, IDLE_TIMEOUT);

			if (debug) console.debug(`Reset idle timeout to ${IDLE_TIMEOUT}`);
		},

		async clearAll({ dispatch, commit }) {
			try {
				await dispatch("clearTimers");
				await signOutEvents();

				commit("clear");
			} catch (error) {
				console.error("Error while clearing state", error);
			}
		},

		async assignUserGroup({ dispatch, commit, state }, { userGroup, eventsToken }) {
			if (!userGroup?.id) return;

			if (eventsToken && eventsToken !== state.eventsToken) {
				commit("setEventsToken", eventsToken);
				await signInEvents(eventsToken);
			}

			commit("setUserGroup", userGroup);

			if (userGroup?.email) {
				try {
					const authType = await getAuthType(userGroup.email);

					const emailProvider = authType?.type || authType?.emailProvider;
					const valid = authType?.valid ?? false;

					await dispatch("assignUserGroupEmail", {
						email: userGroup.email,
						emailProvider: emailProvider || "unknown",
						valid: valid
					});
				} catch (error) {
					await dispatch("assignUserGroupEmail", {
						email: userGroup.email,
						emailProvider: "unknown",
						valid: false
					});
				}
			}
		},

		assignUserGroupEmail({ commit }, { email, emailProvider, valid }) {
			commit("setUserGroupEmail", { email, emailProvider, valid });
		},

		// Never rejects: resolves to whether the group was switched
		async switchUserGroup({ dispatch, commit }, userGroupId) {
			commit("setSwitchingUserGroup", true);

			try {
				await signOutEvents();

				const { data: { userGroup, eventsToken } } = await axios.put("/auth/user-group", { userGroup: userGroupId });

				await dispatch("assignUserGroup", { userGroup, eventsToken });

				return true;
			} catch (err) {
				console.error("Failed to switch user group:", err);
				commit("alerts/add", {
					type: "error",
					message: i18n.t("error.switching-group"),
					timeout: true
				}, { root: true });

				return false;
			} finally {
				commit("setSwitchingUserGroup", false);
			}
		},

		async getAssignedColors({ commit }) {
			try {
				const { data } = await axios.get("/users/colors");
				var colorMap = {};

				data.forEach(color => {
					colorMap[color.userId] = color;
				});
				commit("setColors", colorMap);
			} catch (err) {
				console.error("Failed to get assigned colors", err);
			}
		}
	},
	getters: {
		refreshed: (state) => state.refreshed,
		colors: (state) => state.colors,
		connected: (state) => state.jwt?.length > 0 && Date.now() < state.expires,
		eventsToken: (state) => state.eventsToken,
		profile: (state) => state.profile,
		userStatus: (state) => state.userStatus,
		userGroupId: (state) => state.userGroup?.id,
		userGroup: state => state.userGroup,
		switchingUserGroup: state => state.switchingUserGroup,
		isAdmin: (state, getters) => state.profile.role === roles.ADMIN || getters.isSuperAdmin,
		isSuperAdmin: (state, getters) => state.profile.role === roles.SUPER_ADMIN || getters.isDevops,
		isDevops: (state) => state.profile.role === roles.DEVOPS,
		isMarketing: (state, getters) => state.profile.role === roles.MARKETING || getters.isDevops,
		forgottenEmail: (state) => state.forgottenEmail,
		logoutTrigger: (state) => state.logoutTrigger,
		loggedIn: (state, getters) => getters.connected && !!state.profile?.id && !!state.userGroup?.id,
		awayForwardingEnabled: (state) => state.userGroup?.settings?.awayForwardingSettings?.enabled,
		getField
	}
};
